import {
  DEFAULT_ITEM_STATUS_GROUPS,
  ITEM_STATUS_GROUP_ORDER,
} from "containers/Inventory/itemStatusGroups";
import { Map } from "immutable";
import { StorageSearchValues } from "types/StorageForms/Search";

const convertingToUTCDate = (date: Date) =>
  date &&
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();

const getDateFilter = (value: any) => {
  switch (value) {
    case "today":
      return { "q[gt_days_period]": 1 };
    case "yesterday":
      return { "q[gt_days_period]": 2, "q[lt_days_period]": 1 };
    case "lastWeek":
      return { "q[gt_days_period]": 7 };
    case "lastMonth":
      return { "q[gt_months_period]": 1 };
    default:
      return typeof value === "object"
        ? {
            "q[date_received_at_gteq]": convertingToUTCDate(value.startDate),
            "q[date_received_at_lteq]": convertingToUTCDate(value.endDate),
          }
        : {};
  }
};

// 25kg = 55lbs
export const getMaxWeightValue = (units: string) => (units === "kg" ? 25 : 55);

export const initialValues: StorageSearchValues = {
  weightRange: undefined,
  date: {},
  vendor: null,
  trackingNumber: "",
  hasCustomsDeclarations: null,
  searchMode: "shipments",
};

export const searchFormik = (
  onGetStorageSubmit: (values: any) => void,
  onGetInventoryItemsSubmit: (values: any) => void,
) => ({
  initialValues,
  onSubmit: (values: StorageSearchValues) => {
    const {
      weightRange,
      date,
      vendor,
      trackingNumber: trackingNumberProp,
      hasCustomsDeclarations,
      searchMode,
    } = values;
    const dateFilter = getDateFilter(date);
    const trackingNumber = trackingNumberProp
      ? trackingNumberProp.trim()
      : trackingNumberProp;

    // Filters shared by both search modes.
    const commonFilter = {
      "q[weight_gteq]": weightRange && weightRange[0],
      "q[weight_lteq]": weightRange && weightRange[1],
      "q[vendor_eq]": vendor && vendor.value,
      ...dateFilter,
    };

    // "Items" toggle -> client_api/items, "contains" match on description/comment.
    // The default (empty) view lists in-storage items only. Once the user
    // searches or filters, broaden to packed/shipped items too so they are
    // listed under their own categories. Each category is fetched by its own
    // status query (storageSaga), so `groups` names the categories to ask for
    // rather than a status filter.
    if (searchMode === "items") {
      const itemsFilter = {
        "q[description_or_customer_comment_cont]": trackingNumber || null,
        ...commonFilter,
      };
      const isSearching = Object.values(itemsFilter).some(
        (value) => value !== null && value !== undefined,
      );

      return onGetInventoryItemsSubmit(
        Map({
          page: 1,
          filter: itemsFilter,
          groups: isSearching
            ? ITEM_STATUS_GROUP_ORDER
            : DEFAULT_ITEM_STATUS_GROUPS,
        }),
      );
    }

    // "Shipments" toggle (default) -> client_api/shipments, "contains" match.
    // The customs filter is a Shipment ransack scope, so it is not shared
    // with the items endpoint.
    return onGetStorageSubmit(
      Map({
        page: 1,
        filter: {
          "q[descriptions_or_customer_comment_cont]": trackingNumber || null,
          "q[has_customs_declarations]": hasCustomsDeclarations,
          ...commonFilter,
        },
      }),
    );
  },

  displayName: "StorageSearch",
});
